import { useEffect, useMemo, useRef } from "react";
import debounce from "lodash/debounce";

/**
 * A custom React hook that debounces a callback function in a type-safe way.
 * Works with both standard and async functions.
 *
 * @param callback The function to be debounced.
 * @param delay The debounce delay in milliseconds (defaults to 500).
 * @returns A memoized debounced version of the callback function.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useDebounce = <T extends (...args: any[]) => any>(
  callback: T,
  delay = 500,
) => {
  // Store the latest callback in a ref to avoid recreating the debounce function.
  const callbackRef = useRef<T>(callback);

  // Update the ref's current value whenever the callback changes.
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Create and memoize the debounced function.
  // It only gets recreated if the delay changes.
  return useMemo(() => {
    // This function is what lodash's debounce will call after the delay.
    const debouncedCallback = (...args: Parameters<T>) => {
      callbackRef.current?.(...args);
    };

    return debounce(debouncedCallback, delay);
  }, [delay]);
};
